import { createClient } from "@supabase/supabase-js";

export const SB_URL = import.meta.env.VITE_SUPABASE_URL;
export const SB_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

// ── Official Supabase client (for realtime, auth listeners, storage) ──
export const supabase = createClient(SB_URL, SB_KEY, {
  auth: { persistSession: true, autoRefreshToken: true },
});

// ── Storage helpers ──
export async function storageUpload(bucket, path, file, opts = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    return { data: null, error: { message: "Not authenticated. Please sign in again." } };
  }
  const ct = opts.contentType || file.type || "application/octet-stream";
  try {
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(path, file, { contentType: ct, upsert: true });
    if (error) return { data: null, error: { message: error.message || "Upload failed" } };
    return { data: { path: data.path || path }, error: null };
  } catch (err) {
    return { data: null, error: { message: err.message || "Network error during upload" } };
  }
}

export function storageUrl(bucket, path) {
  return supabase.storage.from(bucket).getPublicUrl(path).data.publicUrl;
}

// ── Realtime subscription helper ──
export function subscribeToTable(table, filter, callback) {
  const channel = supabase.channel(`realtime-${table}-${filter || "all"}`);
  channel.on(
    "postgres_changes",
    { event: "*", schema: "public", table, filter: filter || undefined },
    (payload) => callback(payload),
  ).subscribe();
  return () => supabase.removeChannel(channel);
}

export async function supabaseFetch(table, method = "GET", body = null, params = "") {
  const url = `${SB_URL}/rest/v1/${table}${params}`;
  const headers = {
    apikey: SB_KEY,
    Authorization: `Bearer ${SB_KEY}`,
    "Content-Type": "application/json",
    Prefer: method === "POST" ? "return=representation" : undefined,
  };
  Object.keys(headers).forEach(k => headers[k] === undefined && delete headers[k]);
  const res = await fetch(url, { method, headers, body: body ? JSON.stringify(body) : undefined });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Supabase ${res.status}`);
  }
  return method === "POST" ? res.json() : res;
}

export async function sbAuth(endpoint, body) {
  // Use SDK for login so auto-refresh works properly
  if (endpoint === "token?grant_type=password" && body?.email && body?.password) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: body.email,
      password: body.password,
    });
    if (error) throw new Error(error.message || "Auth failed");
    // Return shape matching the raw REST response for backward compatibility
    return {
      access_token: data.session.access_token,
      refresh_token: data.session.refresh_token,
      expires_in: data.session.expires_in,
      expires_at: data.session.expires_at,
      token_type: "bearer",
      user: data.user,
    };
  }

  // Use SDK for signup too
  if (endpoint === "signup" && body?.email && body?.password) {
    const { data, error } = await supabase.auth.signUp({
      email: body.email,
      password: body.password,
      options: { data: body.data },
    });
    if (error) throw new Error(error.message || "Auth failed");
    return data;
  }

  // Fallback to raw fetch for other endpoints (resend, etc.)
  const res = await fetch(`${SB_URL}/auth/v1/${endpoint}`, {
    method: "POST",
    headers: { apikey: SB_KEY, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const d = await res.json();
  if (d.error || d.error_description)
    throw new Error(d.error_description || d.error?.message || d.msg || "Auth failed");
  return d;
}

/**
 * Check if an email already exists in the profiles table.
 * Uses the anon key (no user session required).
 */
export async function checkEmailExists(email) {
  try {
    const url = `${SB_URL}/rest/v1/profiles?email=eq.${encodeURIComponent(email.trim())}&select=id&limit=1`;
    const res = await fetch(url, {
      headers: { apikey: SB_KEY, Authorization: `Bearer ${SB_KEY}` },
    });
    if (!res.ok) return false;
    const rows = await res.json();
    return Array.isArray(rows) && rows.length > 0;
  } catch {
    return false;
  }
}

/**
 * Send a 6-digit OTP code to an email address.
 * Uses the Supabase SDK signInWithOtp — omitting emailRedirectTo
 * ensures Supabase sends a 6-digit code instead of a magic link.
 */
export async function sbSendOTP(email) {
  const { error } = await supabase.auth.signInWithOtp({
    email: email.trim(),
    options: {
      shouldCreateUser: true,
    },
  });
  if (error) {
    throw new Error(error.message || "Failed to send verification code");
  }
}

/**
 * Verify a 6-digit OTP code. Returns session data (access_token, user) on success.
 */
export async function sbVerifyOTP(email, token) {
  const { data, error } = await supabase.auth.verifyOtp({
    email: email.trim(),
    token: token.trim(),
    type: "email",
  });
  if (error) {
    throw new Error(error.message || "Invalid verification code");
  }
  if (!data.session?.access_token) {
    throw new Error("Verification failed — no session returned");
  }
  return {
    access_token: data.session.access_token,
    refresh_token: data.session.refresh_token,
    user: data.user,
  };
}

/**
 * Get a valid (non-expired) access token.
 * If the current token is expired, the SDK auto-refreshes it.
 */
export async function getValidToken() {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error || !session) return null;
  return session.access_token;
}

/**
 * Call a Supabase RPC function (Postgres function).
 * @param {string} fnName — function name (e.g. "slp_deduct")
 * @param {string} token — auth JWT
 * @param {object} args — function arguments as key-value pairs
 * @returns {*} — function return value
 */
export async function sbRPC(fnName, token, args = {}) {
  const url = `${SB_URL}/rest/v1/rpc/${fnName}`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      apikey: SB_KEY,
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Prefer: "return=representation",
    },
    body: JSON.stringify(args),
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.message || `RPC ${fnName} failed (${res.status})`);
  }
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

/**
 * Insert a notification for any user. Uses Prefer: return=minimal
 * so the caller doesn't need SELECT access on the target user's rows.
 * Uses a 60-second cooldown on table errors instead of permanent disable.
 */
let notifPausedUntil = 0; // timestamp — skip sends until this time
export function resetNotifFlag() { notifPausedUntil = 0; }
export async function sendNotification(token, { userId, type, title, message, metadata }) {
  if (!token || !userId) return;
  if (Date.now() < notifPausedUntil) return;
  const url = `${SB_URL}/rest/v1/notifications`;
  try {
    const body = {
      user_id: userId,
      type: type || "system",
      title: title || "",
      message: message || "",
      is_read: false,
    };
    if (metadata) body.metadata = metadata;
    const res = await fetch(url, {
      method: "POST",
      headers: {
        apikey: SB_KEY,
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Prefer: "return=minimal",
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      // 400 = schema mismatch, 404 = table missing — pause for 60s then retry
      if (res.status === 400 || res.status === 404) notifPausedUntil = Date.now() + 60000;
    }
  } catch {
    // Non-critical — fail silently
  }
}

/**
 * Admin-only: permanently delete a user from auth.users + all data tables.
 * Calls the admin_delete_user RPC (SECURITY DEFINER — runs as postgres).
 * Requires running supabase/admin_delete_user_migration.sql first.
 */
export async function adminDeleteUser(token, userId) {
  const result = await sbRPC("admin_delete_user", token, { p_user_id: userId });
  if (result && !result.success) {
    throw new Error(result.error || "Delete user failed");
  }
  return result || { success: true };
}

export async function sbQuery(table, token, method = "GET", body = null, params = "") {
  const url = `${SB_URL}/rest/v1/${table}${params}`;
  const headers = {
    apikey: SB_KEY,
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  if (method === "POST") headers.Prefer = "return=representation";
  if (method === "PATCH") headers.Prefer = "return=representation";
  const res = await fetch(url, { method, headers, body: body ? JSON.stringify(body) : undefined });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.message || `Error ${res.status}`);
  }
  if (method === "DELETE") return null;
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}
